/** "Send crew to" card (spec 2026-09-01 §D): the server-resolved dispatch
 *  point with address (and its basis, in plain words), pole id, coordinates,
 *  a one-line Copy, and a Google Maps link that uses COORDINATES — a
 *  nearest-transformer proxy address must never route a crew next door. */

import { useEffect, useState } from 'react'
import type { DispatchLocation } from '../../types/oms.ts'
import {
  addressLine, dispatchCopyLine, formatLatLon, googleMapsUrl, targetLabel,
} from './dispatchFormat.ts'

type CopyState = 'idle' | 'copied' | 'failed'

export function DispatchCard({ location }: { location: DispatchLocation | null | undefined }) {
  const [copy, setCopy] = useState<CopyState>('idle')
  useEffect(() => {
    if (copy === 'idle') return
    const t = setTimeout(() => setCopy('idle'), 2000)
    return () => clearTimeout(t)
  }, [copy])

  return (
    <div className="border-t border-gray-100 pt-4">
      <div className="text-[10px] text-gray-400 uppercase tracking-wide mb-1">Send crew to</div>
      {!location ? (
        <div className="text-xs text-gray-400">No dispatch location (cannot place this outage).</div>
      ) : (
        <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900 space-y-0.5">
          <div className="text-[10px] uppercase tracking-wide text-blue-700">
            {targetLabel(location.target)}
          </div>
          {location.device_label && (
            <div className="font-semibold">
              {location.device_type
                ? `${location.device_label} (${location.device_type})`
                : location.device_label}
            </div>
          )}
          {location.pole_id && <div>Pole {location.pole_id}</div>}
          <div>{addressLine(location)}</div>
          <div className="font-mono text-xs text-blue-800">{formatLatLon(location)}</div>
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <button
              type="button"
              onClick={() => {
                // The Clipboard API is absent outside a secure context (and in
                // older browsers), and writeText can throw synchronously — both
                // are a plain "Copy failed", never an unhandled exception.
                try {
                  const write = navigator.clipboard?.writeText
                  if (!write) {
                    setCopy('failed')
                    return
                  }
                  write
                    .call(navigator.clipboard, dispatchCopyLine(location))
                    .then(() => setCopy('copied'), () => setCopy('failed'))
                } catch {
                  setCopy('failed')
                }
              }}
              className="text-xs px-2.5 py-1 rounded-md border border-blue-300 bg-white text-blue-800 hover:bg-blue-100"
            >
              Copy
            </button>
            <a
              href={googleMapsUrl(location)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs px-2.5 py-1 rounded-md bg-blue-600 text-white hover:bg-blue-700"
            >
              Open in Google Maps ↗
            </a>
            {/* Rendered unconditionally so the live region EXISTS before the
                text arrives — a region inserted together with its message is
                not reliably announced. */}
            <span
              aria-live="polite"
              className={copy === 'failed' ? 'text-xs text-red-700' : 'text-xs text-emerald-700'}
            >
              {copy === 'copied' ? 'Copied' : copy === 'failed' ? 'Copy failed' : ''}
            </span>
          </div>
        </div>
      )}
    </div>
  )
}
